import { PermissionDecorator as HIPermissionDecorator } from '@highimpact/react-decorators';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGridPro, type GridColDef, type GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ApiAction, HIConfigurableGridProps, CreateRowConfig, RowActionConfig } from './types';
import { defaultStripedRowClassName, mergeGridSx } from './gridTheme';

const shouldShowItem = (action?: ApiAction) => action === 'display' || action === 'disable';
const isItemDisabled = (action?: ApiAction) => action === 'disable';

function mergeActions<Row extends { id: string | number }>(
  defaults: RowActionConfig<Row>[] = [],
  overrides: HIConfigurableGridProps<Row>['actionOverrides'] = {},
  extras: RowActionConfig<Row>[] = [],
): RowActionConfig<Row>[] {
  const merged = defaults.map((action) => ({ ...action, ...(overrides?.[action.id] ?? {}) }));
  return [...merged, ...extras];
}

function normalizeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.toLowerCase();
  }
  if (value instanceof Date) {
    return value.toISOString().toLowerCase();
  }
  return String(value).toLowerCase();
}

function buildInitialCreateValues<Row extends { id: string | number }>(
  createRowConfig?: CreateRowConfig<Row>,
): Partial<Row> {
  if (!createRowConfig) {
    return {};
  }

  const initialValues = createRowConfig.fields.reduce((acc, field) => {
    acc[field.key] = field.initialValue ?? '';
    return acc;
  }, {} as Record<string, unknown>);

  return initialValues as Partial<Row>;
}

function validateCreateValues<Row extends { id: string | number }>(
  values: Partial<Row>,
  createRowConfig: CreateRowConfig<Row>,
): Partial<Record<keyof Row & string, string>> {
  const errors: Partial<Record<keyof Row & string, string>> = {};

  createRowConfig.fields.forEach((field) => {
    const value = values[field.key];

    if (field.required && (value === undefined || value === null || value === '')) {
      errors[field.key] = 'Required';
      return;
    }

    const customError = field.validate?.(value, values);
    if (customError) {
      errors[field.key] = customError;
    }
  });

  const configErrors = createRowConfig.validate?.(values);
  if (configErrors) {
    Object.entries(configErrors).forEach(([key, message]) => {
      if (message) {
        errors[key as keyof Row & string] = message;
      }
    });
  }

  return errors;
}

export function HIConfigurableGrid<Row extends { id: string | number }>({
  rows,
  loading = false,
  t = (key: string) => key,
  columns,
  defaultRowActions,
  actionOverrides,
  extraRowActions,
  sortControls = [],
  filterControls = [],
  searchPlaceholder,
  initialPageSize = 10,
  createRowConfig,
  enableColumnPermissions = false,
  enableRowActionPermissions = false,
  permissionContext,
  showToolbar = true,
  sx,
  slotProps,
  getRowClassName,
}: HIConfigurableGridProps<Row>) {
  const theme = useTheme();
  const actions = useMemo(
    () => mergeActions(defaultRowActions, actionOverrides, extraRowActions),
    [defaultRowActions, actionOverrides, extraRowActions],
  );

  const [columnActions, setColumnActions] = useState<Partial<Record<string, ApiAction>>>({});
  const [actionPermissions, setActionPermissions] = useState<Partial<Record<string, ApiAction>>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeFilterIds, setActiveFilterIds] = useState<string[]>([]);
  const [activeSortId, setActiveSortId] = useState<string | null>(sortControls[0]?.id ?? null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: Row } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createValues, setCreateValues] = useState<Partial<Row>>(() => buildInitialCreateValues(createRowConfig));
  const [createErrors, setCreateErrors] = useState<Partial<Record<keyof Row & string, string>>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setCreateValues(buildInitialCreateValues(createRowConfig));
    setCreateErrors({});
  }, [createRowConfig]);

  const columnPermissionItems = useMemo(
    () => (enableColumnPermissions ? columns.map((column) => column.permissionItem).filter(Boolean) as string[] : []),
    [columns, enableColumnPermissions],
  );

  const actionPermissionItems = useMemo(
    () => (enableRowActionPermissions ? actions.map((action) => action.permissionItem).filter(Boolean) as string[] : []),
    [actions, enableRowActionPermissions],
  );

  const uniqueColumnPermissionItems = useMemo(
    () => Array.from(new Set(columnPermissionItems)),
    [columnPermissionItems],
  );

  const uniqueActionPermissionItems = useMemo(
    () => Array.from(new Set(actionPermissionItems)),
    [actionPermissionItems],
  );

  const arePermissionsResolved = useMemo(() => {
    const columnsResolved = uniqueColumnPermissionItems.every((item) => columnActions[item] !== undefined);
    const actionsResolved = uniqueActionPermissionItems.every((item) => actionPermissions[item] !== undefined);
    return columnsResolved && actionsResolved;
  }, [uniqueColumnPermissionItems, uniqueActionPermissionItems, columnActions, actionPermissions]);

  const handleColumnPermissionResolve = (item: string, action?: ApiAction) => {
    setColumnActions((prev) => ({ ...prev, [item]: action ?? 'hide' }));
  };

  const handleActionPermissionResolve = (item: string, action?: ApiAction) => {
    setActionPermissions((prev) => ({ ...prev, [item]: action ?? 'hide' }));
  };

  const columnVisibilityModel = useMemo(() => {
    const model: GridColumnVisibilityModel = {};
    columns.forEach((column) => {
      if (!enableColumnPermissions || !column.permissionItem) {
        model[column.field] = true;
        return;
      }
      model[column.field] = shouldShowItem(columnActions[column.permissionItem]);
    });
    return model;
  }, [columns, columnActions, enableColumnPermissions]);

  const searchableColumns = useMemo(
    () => columns.filter((column) => column.searchable !== false),
    [columns],
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return rows.filter((row) => {
      const passesFilters = activeFilterIds.length === 0
        ? true
        : filterControls.some((control) => activeFilterIds.includes(control.id) && control.predicate(row));

      if (!passesFilters) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const matchesSearch = searchableColumns.some((column) => {
        const raw = column.valueGetter ? column.valueGetter(row) : row[column.field];
        const formatted = column.valueFormatter ? column.valueFormatter(raw, row) : normalizeValue(raw);
        return normalizeValue(formatted).includes(normalizedQuery);
      });

      return matchesSearch;
    });
  }, [rows, activeFilterIds, filterControls, searchQuery, searchableColumns]);

  const sortedRows = useMemo(() => {
    const activeSort = sortControls.find((control) => control.id === activeSortId);
    if (!activeSort) {
      return filteredRows;
    }

    const targetColumn = columns.find((column) => column.field === activeSort.field);
    if (!targetColumn) {
      return filteredRows;
    }

    const sorted = [...filteredRows].sort((rowA, rowB) => {
      const valueA = targetColumn.valueGetter ? targetColumn.valueGetter(rowA) : rowA[targetColumn.field];
      const valueB = targetColumn.valueGetter ? targetColumn.valueGetter(rowB) : rowB[targetColumn.field];

      if (targetColumn.sortComparator) {
        return targetColumn.sortComparator(valueA, valueB, rowA, rowB);
      }

      return normalizeValue(valueA).localeCompare(normalizeValue(valueB));
    });

    return activeSort.direction === 'asc' ? sorted : sorted.reverse();
  }, [filteredRows, sortControls, activeSortId, columns]);

  const gridColumns = useMemo<GridColDef[]>(() => {
    const baseColumns: GridColDef[] = columns.map((column) => ({
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      flex: column.flex,
      sortable: column.sortable ?? true,
      filterable: column.filterable ?? true,
      renderCell: column.renderCell ? (params: { row: Row }) => column.renderCell?.(params.row) : undefined,
      valueGetter: column.valueGetter ? (_value: unknown, row: Row) => column.valueGetter?.(row) : undefined,
    }));

    if (actions.length === 0) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: '__actions__',
        headerName: t('Actions'),
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: { row: Row }) => (
          <IconButton size="small" onClick={(event) => setMenuAnchor({ el: event.currentTarget, row: params.row })}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [columns, actions, t]);

  const toggleFilter = (id: string) => {
    setActiveFilterIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((value) => value !== id);
      }
      return [...prev, id];
    });
  };

  const openCreateDialog = () => {
    setCreateValues(buildInitialCreateValues(createRowConfig));
    setCreateErrors({});
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateErrors({});
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    if (searchQuery) {
      setSearchQuery('');
    }
  };

  const handleCreateFieldChange = (key: keyof Row & string, rawValue: string) => {
    const field = createRowConfig?.fields.find((candidate) => candidate.key === key);
    const parsedValue = field?.parse ? field.parse(rawValue) : rawValue;

    setCreateValues((prev) => ({ ...prev, [key]: parsedValue }));
    setCreateErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submitCreateRow = async () => {
    if (!createRowConfig) {
      return;
    }

    const errors = validateCreateValues(createValues, createRowConfig);
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    await createRowConfig.onSubmit(createValues);
    closeCreateDialog();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <>
      {enableColumnPermissions && uniqueColumnPermissionItems.map((item) => (
        <HIPermissionDecorator
          key={`column_${item}`}
          item={item}
          project_id={permissionContext?.project_id}
          site_id={permissionContext?.site_id}
          company_id={permissionContext?.company_id}
          onResolve={handleColumnPermissionResolve}
        />
      ))}

      {enableRowActionPermissions && uniqueActionPermissionItems.map((item) => (
        <HIPermissionDecorator
          key={`action_${item}`}
          item={item}
          project_id={permissionContext?.project_id}
          site_id={permissionContext?.site_id}
          company_id={permissionContext?.company_id}
          onResolve={handleActionPermissionResolve}
        />
      ))}

      {showToolbar && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {createRowConfig && (
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
              >
                {createRowConfig.buttonLabel}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto', alignItems: 'center', justifyContent: 'flex-end' }}>
            {sortControls.map((control) => (
              <Button
                key={control.id}
                variant={activeSortId === control.id ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setActiveSortId(control.id)}
              >
                {control.label}
              </Button>
            ))}

            {filterControls.map((control) => (
              <Button
                key={control.id}
                variant={activeFilterIds.includes(control.id) ? 'contained' : 'outlined'}
                size="small"
                onClick={() => toggleFilter(control.id)}
              >
                {control.label}
              </Button>
            ))}

            {searchOpen ? (
              <>
                <TextField
                  inputRef={searchInputRef}
                  size="small"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={searchPlaceholder ?? t('Search...')}
                  sx={{ minWidth: 240 }}
                />
                <CloseIcon fontSize="small" onClick={handleSearchClose} sx={{ cursor: 'pointer' }} />
              </>
            ) : (
              <SearchIcon fontSize="small" onClick={() => setSearchOpen(true)} sx={{ cursor: 'pointer' }} />
            )}
          </Box>
        </Box>
      )}

      {arePermissionsResolved ? (
        <DataGridPro
          rows={sortedRows}
          columns={gridColumns}
          getRowId={(row: Row) => row.id}
          columnVisibilityModel={columnVisibilityModel}
          disableRowSelectionOnClick
          pagination
          sx={mergeGridSx(theme, sx)}
          slotProps={slotProps}
          getRowClassName={getRowClassName ?? defaultStripedRowClassName}
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: initialPageSize,
              },
            },
          }}
          pageSizeOptions={[5, 10, 20, 50]}
          slots={{
            noRowsOverlay: () => (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="body2">{t('No data')}</Typography>
              </Box>
            ),
          }}
        />
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
          <CircularProgress size={22} />
        </Box>
      )}

      {menuAnchor && (
        <Menu
          anchorEl={menuAnchor.el}
          open
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actions.map((action) => {
            const row = menuAnchor.row;
            const isVisibleByCondition = action.show ? action.show(row) : true;
            if (!isVisibleByCondition) {
              return null;
            }

            const permissionState = enableRowActionPermissions && action.permissionItem
              ? actionPermissions[action.permissionItem]
              : 'display';

            if (!shouldShowItem(permissionState)) {
              return null;
            }

            const disabled = isItemDisabled(permissionState) || (action.disabled ? action.disabled(row) : false);

            const menuItem = (
              <MenuItem
                key={action.id}
                disabled={disabled}
                onClick={() => {
                  void action.onClick(row);
                  setMenuAnchor(null);
                }}
              >
                {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            );

            if (!enableRowActionPermissions || !action.permissionItem || !action.permissionExtra) {
              return menuItem;
            }

            return (
              <HIPermissionDecorator
                key={`${action.id}_${String(row.id)}`}
                item={action.permissionItem}
                extra={action.permissionExtra(row)}
              >
                {menuItem}
              </HIPermissionDecorator>
            );
          })}
        </Menu>
      )}

      {createRowConfig && (
        <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
          <DialogTitle>{createRowConfig.dialogTitle ?? createRowConfig.buttonLabel}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            {createRowConfig.fields.map((field) => {
              const fieldValue = createValues[field.key];
              const fieldError = createErrors[field.key];

              return (
                <TextField
                  key={field.key}
                  select={field.type === 'select'}
                  type={field.type === 'number' || field.type === 'date' ? field.type : 'text'}
                  label={field.label}
                  placeholder={field.placeholder}
                  value={fieldValue ?? ''}
                  onChange={(event) => handleCreateFieldChange(field.key, event.target.value)}
                  error={Boolean(fieldError)}
                  helperText={fieldError}
                  fullWidth
                  required={field.required}
                  slotProps={field.type === 'date' ? { inputLabel: { shrink: true } } : undefined}
                >
                  {field.type === 'select' && (field.options ?? []).map((option) => (
                    <MenuItem key={`${field.key}_${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateDialog}>{createRowConfig.cancelLabel ?? t('Cancel')}</Button>
            <Button variant="contained" onClick={() => void submitCreateRow()}>
              {createRowConfig.submitLabel ?? t('Create')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
